<template>
  <section class="record-view">
    <AppPageTitleBar :title="pageTitle" :back-to="backRoute" back-label="Natrag">
      <template #actions>
        <v-btn
          v-if="record && !loadError && record[`${stage}CompletedAt`]"
          class="button button--secondary"
          :loading="isDownloadingPdf"
          :disabled="isDownloadingPdf"
          prepend-icon="mdi-download-outline"
          variant="outlined"
          @click="downloadPdfAsync"
        >Preuzmi PDF</v-btn>
      </template>
    </AppPageTitleBar>

    <v-progress-linear v-if="isLoading" indeterminate />
    <v-alert v-else-if="loadError" type="error" variant="tonal">{{ loadError }}</v-alert>

    <div v-else class="form page-stack grid-stack grid-gap-page-section">
      <RecordVehicleSection :vehicle-id="record.vehicleId" :vehicle="vehicle" readonly />
      <RecordEquipmentSection :equipment="record.equipment ?? []" readonly />
      <RecordUserSection :values="pickupValues" readonly />
      <RecordUsageSection :values="pickupValues" readonly />
      <RecordPickupConditionSection :values="pickupValues" readonly />
      <RecordReturnConditionSection
        v-if="isReturnView"
        :values="returnValues"
        :driven-duration="drivenDuration"
        readonly
      />
      <RecordPhotosSection :saved-photos="stagePhotos" readonly preview @preview="openPhotoAsync" />

      <section class="page-panel">
        <header class="page-panel__header"><h3 class="panel-title">Potpisi</h3></header>
        <div class="page-panel__content grid-stack grid-row-gap-18">
          <v-checkbox class="checkbox" density="compact" hide-details :model-value="true" readonly>
            <template #label>
              Pročitao sam i prihvatio opće uvjete otisnute na poleđini te izjavljujem da sam suglasan da su oni sastavni dio&nbsp;
              <router-link class="record-view__terms-link" :to="termsRoute" @click.stop>zapisnika</router-link>.
            </template>
          </v-checkbox>
          <div class="grid grid-column-gap-16 grid-row-gap-18">
            <div class="col-12 col-sm-6">
              <p class="control-label">Potpis korisnika pri {{ isReturnView ? 'vraćanju' : 'preuzimanju' }} vozila</p>
              <img v-if="userSignature" class="record-view__signature" :src="userSignature" alt="Potpis korisnika" />
              <p v-else>Potpis nije dostupan.</p>
            </div>
            <div class="col-12 col-sm-6">
              <p class="control-label">Potpis Porsche Croatia pri {{ isReturnView ? 'vraćanju' : 'preuzimanju' }} vozila</p>
              <img v-if="companySignature" class="record-view__signature" :src="companySignature" alt="Potpis Porsche Croatia" />
              <p v-else>Potpis nije dostupan.</p>
            </div>
          </div>
        </div>
      </section>
    </div>

    <v-dialog :model-value="Boolean(photoPreviewUrl)" width="fit-content" max-width="1200" @update:model-value="!$event && closePhotoPreview()">
      <div class="page-panel record-view__preview">
        <div class="record-view__preview-header">
          <span>{{ photoPreviewName }}</span>
          <v-btn aria-label="Zatvori fotografiju" icon="mdi-close" size="small" variant="text" @click="closePhotoPreview" />
        </div>
        <img v-if="photoPreviewUrl && !photoPreviewError" class="record-view__preview-image" :src="photoPreviewUrl" :alt="photoPreviewName" @error="photoPreviewError = true" />
        <p v-else>Pregled ove fotografije nije dostupan u pregledniku. <a :href="photoPreviewUrl" :download="photoPreviewName">Preuzmi fotografiju</a>.</p>
      </div>
    </v-dialog>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import RecordEquipmentSection from '../../components/records/RecordEquipmentSection.vue'
import RecordPhotosSection from '../../components/records/RecordPhotosSection.vue'
import RecordPickupConditionSection from '../../components/records/RecordPickupConditionSection.vue'
import RecordReturnConditionSection from '../../components/records/RecordReturnConditionSection.vue'
import RecordUsageSection from '../../components/records/RecordUsageSection.vue'
import RecordUserSection from '../../components/records/RecordUserSection.vue'
import RecordVehicleSection from '../../components/records/RecordVehicleSection.vue'
import { downloadResponseFileAsync } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showErrorToast, showRequestErrorToast } from '../../store/appStore'

const route = useRoute()
const record = ref(null)
const isLoading = ref(true)
const loadError = ref('')
const isDownloadingPdf = ref(false)
const isOpeningPhoto = ref(false)
const photoPreviewUrl = ref('')
const photoPreviewName = ref('')
const photoPreviewError = ref(false)
let latestLoadRequestId = 0
let latestPhotoRequestId = 0

const isReturnView = computed(() => route.name === 'fleet-record-return-view')
const stage = computed(() => isReturnView.value ? 'return' : 'pickup')
const backRoute = computed(() => ({
  name: 'fleet-forms',
  query: {
    ...(route.query.page ? { page: route.query.page } : {}),
    ...(route.query.search ? { search: route.query.search } : {}),
    ...(route.query.brandCode ? { brandCode: route.query.brandCode } : {}),
  },
}))
const pageTitle = computed(() => {
  const title = isReturnView.value ? 'Zapisnik o vraćanju' : 'Zapisnik o preuzimanju'
  return record.value ? `${title} — ${record.value.vehicleName} ${record.value.vehiclePlate}` : title
})
const vehicle = computed(() => record.value && ({
  brandCode: record.value.vehicleBrandCode,
  name: record.value.vehicleName,
  plate: record.value.vehiclePlate,
  vin: record.value.vehicleVin,
  color: record.value.vehicleColor,
}))
const pickupValues = computed(() => ({
  ...record.value,
  pickupMileageKm: record.value?.pickupForm.mileageKm ?? '',
  pickupDate: record.value?.pickupForm.date ?? '',
  pickupTime: record.value?.pickupForm.time ?? '',
  pickupBatteryPercent: record.value?.pickupForm.batteryPercent ?? '',
  pickupFuelLevel: record.value?.pickupForm.fuelLevel ?? '',
  pickupDamage: record.value?.pickupForm.damage ?? '',
}))
const returnValues = computed(() => ({
  returnMileageKm: record.value?.returnForm?.mileageKm ?? '',
  returnDate: record.value?.returnForm?.date ?? '',
  returnTime: record.value?.returnForm?.time ?? '',
  returnBatteryPercent: record.value?.returnForm?.batteryPercent ?? '',
  returnFuelLevel: record.value?.returnForm?.fuelLevel ?? '',
  returnDamage: record.value?.returnForm?.damage ?? '',
}))
const stagePhotos = computed(() => record.value?.[`${stage.value}Form`]?.photos ?? [])
const userSignature = computed(() => record.value?.[`${stage.value}Form`]?.userSignature)
const companySignature = computed(() => record.value?.[`${stage.value}Form`]?.companySignature)
const termsRoute = computed(() => ({ name: 'fleet-record-pickup-terms', query: { returnTo: route.fullPath } }))
const drivenDuration = computed(() => {
  const days = record.value?.returnForm?.drivenDays
  const hours = record.value?.returnForm?.drivenHours
  if (days == null || hours == null) return { days: '', hours: '', dayUnit: 'dana', hourUnit: 'sati' }

  const plurals = new Intl.PluralRules('hr')
  const hourUnit = { one: 'sat', few: 'sata', other: 'sati' }[plurals.select(hours)]
  return { days: String(days), hours: String(hours), dayUnit: plurals.select(days) === 'one' ? 'dan' : 'dana', hourUnit }
})

watch(() => [route.name, route.params.recordId], loadRecordAsync, { immediate: true })
onBeforeUnmount(() => {
  latestLoadRequestId++
  latestPhotoRequestId++
  closePhotoPreview()
})

async function loadRecordAsync () {
  const requestId = ++latestLoadRequestId
  const recordId = route.params.recordId
  const returnView = isReturnView.value
  latestPhotoRequestId++
  isOpeningPhoto.value = false
  closePhotoPreview()
  record.value = null
  loadError.value = ''
  isLoading.value = true

  try {
    const [detail, returnRecord] = await Promise.all([
      apiClient.getAsync(`/api/drivepark/records/${recordId}/pickup`),
      ...(returnView ? [apiClient.getAsync(`/api/drivepark/records/${recordId}/return`)] : []),
    ])
    if (requestId !== latestLoadRequestId) return

    const allowedStatus = returnView
      ? detail.status === 'returned'
      : ['picked-up', 'returned'].includes(detail.status)

    if (!allowedStatus) {
      loadError.value = 'Ovaj zapisnik još nije dovršen.'
      return
    }

    record.value = returnView ? { ...detail, returnForm: returnRecord.returnForm } : detail
  } catch (error) {
    console.error('Failed to load the completed drive park record.', error)
    if (requestId === latestLoadRequestId) loadError.value = 'Zapisnik nije bilo moguće učitati.'
  } finally {
    if (requestId === latestLoadRequestId) isLoading.value = false
  }
}

async function downloadPdfAsync () {
  if (isDownloadingPdf.value) return
  isDownloadingPdf.value = true

  try {
    const response = await apiClient.getAsync(
      `/api/drivepark/records/${record.value.id}/pdf/${stage.value}`,
      { responseType: 'response', headers: { Accept: 'application/pdf' } }
    )
    await downloadResponseFileAsync(response, `Zapisnik-o-${stage.value === 'pickup' ? 'preuzimanju' : 'vracanju'}-${record.value.id}.pdf`)
  } catch (error) {
    console.error(`Failed to download the ${stage.value} record PDF.`, error)
    if (error.status === 404) showErrorToast('PDF zapisnika nije dostupan.')
    else showRequestErrorToast(error, 'PDF zapisnika nije bilo moguće preuzeti.')
  } finally {
    isDownloadingPdf.value = false
  }
}

async function openPhotoAsync (photo) {
  if (isOpeningPhoto.value) return
  const requestId = latestPhotoRequestId
  const recordId = record.value.id
  isOpeningPhoto.value = true

  try {
    const image = await apiClient.getAsync(
      `/api/drivepark/records/${recordId}/photos/${photo.id}/download`,
      { responseType: 'blob', headers: { Accept: 'image/jpeg, image/png, image/heic' } }
    )
    if (requestId !== latestPhotoRequestId) return
    closePhotoPreview()
    photoPreviewName.value = photo.fileName
    photoPreviewError.value = false
    photoPreviewUrl.value = URL.createObjectURL(image)
  } catch (error) {
    console.error('Failed to open a drive park record photo.', error)
    if (requestId === latestPhotoRequestId) showRequestErrorToast(error, 'Fotografiju nije bilo moguće otvoriti.')
  } finally {
    if (requestId === latestPhotoRequestId) isOpeningPhoto.value = false
  }
}

function closePhotoPreview () {
  if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value)
  photoPreviewUrl.value = ''
}
</script>

<style scoped lang="scss">
.record-view {
  :deep(.page-panel__header) {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-border);
  }

  &__preview-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__terms-link {
    text-decoration: underline;
  }

  &__signature {
    display: block;
    width: 100%;
    height: 160px;
    object-fit: contain;
    border: 2px dashed var(--color-border-soft);
    border-radius: 10px;
    background: var(--color-surface-hover);
  }

  &__preview-header {
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__preview-image {
    display: block;
    max-width: 100%;
    max-height: calc(100dvh - 160px);
    margin: auto;
    object-fit: contain;
  }
}
</style>
